/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { Janus } from 'janus-gateway'
import { JanusJS } from 'janus-gateway/npm/janus'
import { UserDisplayInfo, Participant } from '@/webrtc/types'
import { WebRTC } from '@/webrtc'
import { ResponsePublisher } from './types'
import { WebRTCEventTypes } from '@/webrtc/constants/eventTypes'
import { decamelizeKeys } from '@/utils'

export const initJanus = () => {
  return new Promise<void>(resolve => {
    Janus.init({
      debug: process.env.NODE_ENV === 'production' ? false : 'all',
      callback: () => resolve()
    })
  })
}

export const createSession = (
  server: string | Array<string>,
  apiSecret: string,
  webrtc: WebRTC
) => {
  let localHandle: any
  let privateId: string | undefined

  const session = new Janus({
    server,
    apisecret: apiSecret,
    success: () => {
      session.attach({
        plugin: 'janus.plugin.videoroom',
        success: (handle: any) => {
          localHandle = handle
          webrtc.events.emit(WebRTCEventTypes.SESSION_SUCCEEDED, handle)
          Janus.listDevices((devices: MediaDeviceInfo[]) =>
            webrtc.events.emit(WebRTCEventTypes.DEVICE_LISTED, devices)
          )
        },
        onmessage: (message: any, jsep?: JanusJS.JSEP) => {
          const event = message.videoroom as string

          if (!event) {
            return
          }

          Janus.debug('Event: ' + event)

          if (event == 'event' && message['error_code']) {
            webrtc.events.emit(
              WebRTCEventTypes.JANUS_ERROR,
              Number(message['error_code'])
            )
          }

          if (event == 'joined') {
            localHandle.createOffer({
              media: {
                audioRecv: false,
                videoRecv: false,
                audioSend: true,
                videoSend: webrtc.defaultVideoEnable
              },
              simulcast: false,
              simulcast2: false,
              success: (jsep: any) => {
                localHandle.send({
                  message: {
                    request: 'configure',
                    audio: true,
                    video: true
                  },
                  jsep
                })
              }
            })

            privateId = message.private_id

            if (message.publishers) {
              subscribeAllRemoteStream(message.publishers)
            }
          } else if (event == 'event') {
            if (message.publishers) {
              subscribeAllRemoteStream(message.publishers)
            } else if (message.leaving) {
              if (message.leaving !== 'ok') {
                webrtc.events.emit(
                  WebRTCEventTypes.REMOTE_LEAVED,
                  // リモートの場合、idが入る
                  Number(message.leaving)
                )
              }
            }
          }

          if (jsep) {
            localHandle.handleRemoteJsep({ jsep })
          }
        },
        onlocalstream: (stream: MediaStream) => {
          webrtc.events.emit(WebRTCEventTypes.LOCAL_STREAM, stream)
        },
        mediaState: (mediaType: string, state: boolean) => {
          webrtc.events.emit(
            WebRTCEventTypes.MEDIA_STATE_CHANGED,
            mediaType,
            state
          )
        }
      })
    },
    destroyed: () => {
      webrtc.events.emit(WebRTCEventTypes.SESSION_DESTROYED)
    }
  })

  const subscribeAllRemoteStream = (publishers: Array<ResponsePublisher>) => {
    publishers.forEach(publisher => {
      subscribeRemoteStream(
        publisher.id,
        publisher.display,
        publisher.audio_codec,
        publisher.video_codec
      )
    })
  }

  const subscribeRemoteStream = (
    id: string,
    display: string,
    audio: string,
    video: string
  ) => {
    let remoteHandle: any = null
    let participant: Participant | undefined

    session.attach({
      plugin: 'janus.plugin.videoroom',
      success: (handle: any) => {
        const message = decamelizeKeys({
          request: 'join',
          room: webrtc.roomId,
          pin: webrtc.pinCode,
          ptype: 'subscriber',
          feed: id,
          privateId: privateId
        })
        remoteHandle = handle
        remoteHandle.simulcastStarted = false
        remoteHandle.videoCodec = video
        remoteHandle.send({ message })
      },
      error: (error: any) => {
        Janus.error('  -- Error attaching plugin...', error)
      },
      onmessage: (message: any, jsep?: JanusJS.JSEP) => {
        const event = message.videoroom

        if (message.error) {
          Janus.error('  -- Error attaching plugin...', message.error)
        }

        if (event === 'attached') {
          try {
            participant = {
              id: Number(message.id),
              ...(JSON.parse(message.display) as UserDisplayInfo)
            }
          } catch (error) {
            console.error('Parsing user display info failed')
          }

          webrtc.events.emit(
            WebRTCEventTypes.REMOTE_JOINED,
            remoteHandle,
            participant || {}
          )
        }

        if (jsep) {
          remoteHandle.createAnswer({
            jsep,
            media: { audioSend: false, videoSend: false },
            success: (jsep: JanusJS.JSEP) => {
              remoteHandle.send({
                message: {
                  request: 'start',
                  room: webrtc.roomId,
                  pin: webrtc.pinCode
                },
                jsep
              })
            },
            error: (error: any) => {
              Janus.error('WebRTC error:', error)
            }
          })
        }
      },
      onremotestream: (stream: MediaStream) => {
        webrtc.events.emit(
          WebRTCEventTypes.REMOTE_STREAM,
          participant || {},
          stream
        )
      }
    })
  }

  return session
}
